import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  ShieldCheck,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  Users,
  Sparkles,
  MailCheck,
  MailWarning,
} from 'lucide-react';
import type { User } from '../../schemas/validation';
import { cn } from '../../utils/cn';
import { formatDate, type UserFilterOption } from './utils';

interface UserTableProps {
  users: User[];
  currentUser: User | null;
  mimicingUserId: string | null;
  searchTerm: string;
  filterRole: UserFilterOption;
  onMimicUser: (user: User) => void;
  onBuildRoaryForUser: (user: User) => void;
  onToggleAdmin: (user: User) => void;
  onEditUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  currentUser,
  mimicingUserId,
  searchTerm,
  filterRole,
  onMimicUser,
  onBuildRoaryForUser,
  onToggleAdmin,
  onEditUser,
  onDeleteUser,
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <colgroup>
          <col className="w-1/4" />
          <col className="w-[100px]" />
          <col className="w-[110px]" />
          <col className="w-[100px]" />
          <col className="w-[100px]" />
          <col />
        </colgroup>
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              User
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Role
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Verified
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Last Updated
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map(user => (
            <tr key={user.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4 min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {user.name}
                      {user.id === currentUser?.id && (
                        <span className="ml-2 text-xs text-gray-500">(You)</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 truncate">{user.email}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    user.isAdmin ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                  )}
                >
                  {user.isAdmin ? (
                    <>
                      <ShieldCheck size={12} className="mr-1" />
                      Administrator
                    </>
                  ) : (
                    <>
                      <Shield size={12} className="mr-1" />
                      User
                    </>
                  )}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    user.emailVerified
                      ? 'bg-green-100 text-green-800'
                      : 'bg-amber-100 text-amber-800'
                  )}
                >
                  {user.emailVerified ? (
                    <>
                      <MailCheck size={12} className="mr-1" />
                      Verified
                    </>
                  ) : (
                    <>
                      <MailWarning size={12} className="mr-1" />
                      Pending
                    </>
                  )}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {formatDate(user.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {formatDate(user.updatedAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end space-x-2">
                  <button
                    onClick={() => onMimicUser(user)}
                    disabled={user.id === currentUser?.id || mimicingUserId === user.id}
                    className={cn(
                      'btn-ghost text-xs text-amber-600 hover:text-amber-700 flex items-center',
                      (user.id === currentUser?.id || mimicingUserId === user.id) &&
                        'opacity-50 cursor-not-allowed'
                    )}
                    title="View as this user"
                  >
                    {mimicingUserId === user.id ? (
                      'Loading...'
                    ) : (
                      <>
                        <UserCheck size={14} className="mr-1" />
                        Mimic
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => onBuildRoaryForUser(user)}
                    disabled={user.id === currentUser?.id || mimicingUserId === user.id}
                    className={cn(
                      'btn-ghost text-xs text-orange-600 hover:text-orange-700 flex items-center',
                      (user.id === currentUser?.id || mimicingUserId === user.id) &&
                        'opacity-50 cursor-not-allowed'
                    )}
                    title="Mimic this user and open Roary to build them a website"
                  >
                    <Sparkles size={14} className="mr-1" />
                    Build with Roary
                  </button>
                  <button
                    onClick={() => onToggleAdmin(user)}
                    disabled={user.id === currentUser?.id}
                    className={cn(
                      'btn-ghost text-xs w-[110px] text-center justify-center',
                      user.id === currentUser?.id && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    {user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                  </button>
                  <div className="flex items-center space-x-1">
                    <Link to={`/admin/users/${user.id}`} className="btn-ghost text-xs">
                      <Eye size={14} />
                    </Link>
                    <button onClick={() => onEditUser(user)} className="btn-ghost text-xs">
                      <Edit size={14} />
                    </button>
                    <button
                      onClick={() => onDeleteUser(user)}
                      disabled={user.id === currentUser?.id}
                      className={cn(
                        'btn-ghost text-xs text-red-600 hover:text-red-700',
                        user.id === currentUser?.id && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {users.length === 0 && (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterRole !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by adding your first user.'}
          </p>
        </div>
      )}
    </div>
  );
};
